import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { IColorOption } from '@opengov/stories-shared/types';
import Color from 'color';
import { isNumber } from 'lodash';
import React, { useMemo } from 'react';

import { colorSelect as msgs } from '../../../../locale/stories.en.locale.json';
import { ColorSelect } from '../ColorSelect';
import { styles } from './ContrastColorsSettings.styles';

export type IContrastColorsSettingsProps = {
  dataTest?: string;
  primaryColorTitle: string;
  secondaryColorTitle: string;
  primaryColors: IColorOption[];
  secondaryColors: IColorOption[];
  secondaryMaxColorLength?: number;
  primaryMaxColorLength?: number;
  pageBgColorCode?: string;
  onChangePrimaryColor(colors: IColorOption[]): void;
  onChangeSecondaryColor(colors: IColorOption[]): void;
};

type IGetAccessabilityDetailsTextParams = {
  isAccessible: boolean;
  contrastRatio: number;
  primaryColorTitle: string;
  secondaryColorTitle: string;
};

const getAccessabilityDetailsText = ({
  isAccessible,
  contrastRatio,
  primaryColorTitle,
  secondaryColorTitle
}: IGetAccessabilityDetailsTextParams) =>
  msgs[isAccessible ? 'accessibleDetails' : 'notAccessibleDetails']
    .replace('{primaryColorTitle}', primaryColorTitle.toLowerCase())
    .replace('{secondaryColorTitle}', secondaryColorTitle.toLowerCase())
    .replace('{contrastRatio}', contrastRatio.toFixed(2));

export const ContrastColorsSettings = (props: IContrastColorsSettingsProps) => {
  const {
    dataTest = 'contrast-colors-settings',
    primaryColorTitle,
    secondaryColorTitle,
    primaryMaxColorLength,
    secondaryMaxColorLength,
    pageBgColorCode,
    onChangePrimaryColor,
    onChangeSecondaryColor,
    primaryColors,
    secondaryColors
  } = props;

  const contrastRatio = useMemo<number | null>(() => {
    const primaryColor = primaryColors?.find((c) => c.selected)?.colorCode;
    const secondaryColor = secondaryColors?.find((c) => c.selected)?.colorCode;
    if (!primaryColor || !secondaryColor) return null;
    return Color(primaryColor).contrast(Color(secondaryColor));
  }, [primaryColors, secondaryColors]);

  const isAccessible = contrastRatio >= 3;

  return (
    <Box data-test={dataTest} sx={styles.container}>
      <ColorSelect
        colors={primaryColors}
        title={primaryColorTitle}
        maxColorBarLength={primaryMaxColorLength}
        pageBgColorCode={pageBgColorCode}
        onChange={onChangePrimaryColor}
      />

      <ColorSelect
        colors={secondaryColors}
        title={secondaryColorTitle}
        maxColorBarLength={secondaryMaxColorLength}
        pageBgColorCode={pageBgColorCode}
        onChange={onChangeSecondaryColor}
      />

      {isNumber(contrastRatio) && (
        <Box sx={styles.contrastRatio} data-test="accessability-info">
          <Chip
            label={isAccessible ? msgs.accessible : msgs.notAccessible}
            color={isAccessible ? 'success' : 'error'}
            variant="strong"
            size="small"
            data-test={isAccessible ? 'positive-badge' : 'negative-badge'}
          />
          <Typography variant="caption">
            {getAccessabilityDetailsText({
              isAccessible,
              contrastRatio,
              primaryColorTitle,
              secondaryColorTitle
            })}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
